import React, { useState } from 'react';
import { MapPin, ChevronRight } from 'lucide-react';

/**
 * <MiniMatcherStateChip>
 * ──────────────────────────────────────────────────────────────
 * Shared persistent state-chip used at the top of every Mini
 * Matcher body (Builder / H&L / Home Designs). Same visual
 * treatment across all three surfaces so users never wonder
 * which state is being matched.
 *
 * Behaviour:
 *  - State unknown → "We'll ask in a moment" badge (no input).
 *    The actual state pill question (Q1) is then captured by the
 *    matcher's own question flow.
 *  - State known → clickable "Matching for: NSW" pill that opens
 *    an inline `<select>` for quick override.
 *
 * Styling: white text, translucent white pill, hairline border,
 * 6px backdrop blur. Centred to the form via the flex wrapper.
 * Reads cleanly on every matcher background (light, dark, violet,
 * photo overlay).
 * ──────────────────────────────────────────────────────────────
 */

const AU_STATES = ['SA', 'VIC', 'NSW', 'QLD', 'WA', 'TAS', 'ACT', 'NT'];

const MiniMatcherStateChip = ({ state, region, onChange, testId = 'mm-state-chip' }) => {
  const [editing, setEditing] = useState(false);
  const known = !!state;
  // Region is shown after state with a thin middot separator. Empty
  // string / null / undefined → omitted so the chip stays clean while
  // the user is still on Q1.
  const regionLabel = region && typeof region === 'string' ? region.trim() : '';
  return (
    <div style={{ display: 'flex', justifyContent: 'center', margin: '0 0 18px 0' }}>
      <div
        data-testid={testId}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '6px 12px 6px 10px',
          borderRadius: 999,
          background: known ? 'rgba(29,142,254,0.18)' : 'rgba(255,255,255,0.14)',
          border: `1px solid ${known ? 'rgba(29,142,254,0.35)' : 'rgba(255,255,255,0.25)'}`,
          color: '#ffffff',
          fontSize: 12,
          fontWeight: 500,
          lineHeight: 1,
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
        }}
      >
        <MapPin size={13} strokeWidth={2.25} />
        {!known ? (
          <span data-testid={`${testId}-prompt`}>We&rsquo;ll ask in a moment</span>
        ) : !editing ? (
          <button
            type="button"
            onClick={() => setEditing(true)}
            data-testid={`${testId}-button`}
            style={{
              background: 'none',
              border: 0,
              padding: 0,
              color: 'inherit',
              font: 'inherit',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            Matching for&nbsp;<strong style={{ fontWeight: 700 }}>{state}</strong>
            {regionLabel ? (
              <>
                &nbsp;<span style={{ opacity: 0.55, fontWeight: 400 }}>·</span>&nbsp;
                <strong style={{ fontWeight: 700 }} data-testid={`${testId}-region`}>{regionLabel}</strong>
              </>
            ) : null}
            <ChevronRight size={11} style={{ opacity: 0.7 }} />
          </button>
        ) : (
          <select
            autoFocus
            value={state}
            data-testid={`${testId}-select`}
            onChange={(e) => {
              onChange?.(e.target.value);
              setEditing(false);
            }}
            onBlur={() => setEditing(false)}
            style={{
              background: 'transparent',
              border: 0,
              padding: 0,
              color: 'inherit',
              font: 'inherit',
              outline: 'none',
              cursor: 'pointer',
            }}
          >
            <option value="" style={{ color: '#1d1d1f' }}>Select state…</option>
            {AU_STATES.map((s) => (
              <option key={s} value={s} style={{ color: '#1d1d1f' }}>{s}</option>
            ))}
          </select>
        )}
      </div>
    </div>
  );
};

export default MiniMatcherStateChip;
